import { defineStore } from 'pinia';
import { db, functions } from '../plugins/firebase';
import { collection, getDocs, addDoc, query, where } from 'firebase/firestore';
import { getAuth } from 'firebase/auth'; // Use getAuth for authentication
import { httpsCallable } from "firebase/functions";

export const usePaymentStore = defineStore('payment', {
  state: () => ({
    data: {
      payments: [],
      subscriptions: [],
    },
    selectedProcessor: '', // Dynamically set payment processor
  }),

  actions: {
    /**
     * Ensure the user is authenticated before performing actions.
     * @returns {string} - The authenticated user's ID.
     * @throws {Error} - If the user is not authenticated.
     */
    ensureAuthenticated() {
  const auth = getAuth();
  const userId = auth.currentUser?.uid;

  if (!userId) {
    console.warn('User is not authenticated. Skipping authenticated actions.');
    throw new Error('User not authenticated. Please log in to continue.');
  }

  return userId;
},

    /**
     * Fetch all payments for the authenticated user.
     */
    async fetchPayments() {
      try {
        const userId = this.ensureAuthenticated();
        const paymentsCollection = collection(db, 'payments');
        const q = query(paymentsCollection, where('userId', '==', userId));
        const snapshot = await getDocs(q);
        this.data.payments = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      } catch (error) {
        console.error('Error fetching payments:', error);
      }
    },

    /**
     * Fetch all subscriptions for the authenticated user.
     */
    async fetchSubscriptions() {
      try {
        const userId = this.ensureAuthenticated();
        const subscriptionsCollection = collection(db, 'subscriptions');
        const q = query(subscriptionsCollection, where('userId', '==', userId));
        const snapshot = await getDocs(q);
        this.data.subscriptions = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      } catch (error) {
        console.error('Error fetching subscriptions:', error);
      }
    },

    /**
     * Create a Stripe payment using Firebase Functions.
     * @param {Object} paymentData - The data for the payment.
     */
    async createStripePayment(paymentData) {
      try {
        const callable = functions.httpsCallable('createStripePayment');
        const result = await callable(paymentData);
        return result.data;
      } catch (error) {
        console.error('Error creating Stripe payment:', error);
        throw error;
      }
    },

    /**
     * Create a PayPal order using Firebase Functions.
     * @param {Object} paymentData - The data for the payment.
     */
    async createPaypalOrder(paymentData) {
      try {
        const callable = functions.httpsCallable('createPaypalOrder');
        const result = await callable(paymentData);
        return result.data;
      } catch (error) {
        console.error('Error creating PayPal order:', error);
        throw error;
      }
    },

    /**
     * Save a subscription to the database with user ID.
     * @param {Object} subscriptionData - The data for the subscription.
     */
    async saveSubscription(subscriptionData) {
      try {
        const userId = this.ensureAuthenticated();
        const subscriptionWithUser = {
          ...subscriptionData,
          userId,
        };

        const subscriptionsCollection = collection(db, 'subscriptions');
        await addDoc(subscriptionsCollection, subscriptionWithUser);
      } catch (error) {
        console.error('Error saving subscription:', error);
      }
    },

    /**
     * Set the selected payment processor.
     * @param {string} processor - The name of the payment processor (e.g., 'stripe', 'paypal').
     */
    setProcessor(processor) {
      this.selectedProcessor = processor;
      localStorage.setItem('selectedProcessor', processor); // Persist in local storage
    },

    /**
     * Load the selected processor from local storage (if any).
     */
    loadSelectedProcessor() {
      const processor = localStorage.getItem('selectedProcessor');
      if (processor) {
        this.selectedProcessor = processor;
      }
    },
  },
});